/**
 * AllergenTagFilterBody Component
 *
 * Renders allergen tag icon chips (allium, barley, dairy, egg, shellfish, soy, nuts, wheat, fish)
 * inside the FiltersModal body for the Tags tab.
 * Each chip toggles its tag in/out of the selected set.
 */

// React
import { useMemo } from 'react';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { CHIP_UNSELECTED, CHIP_SELECTED } from './filterChipStyles';

// Icons
import {
  AlliumIcon,
  BarleyIcon,
  DairyIcon,
  EggIcon,
  ShellFishIcon,
  SoyIcon,
  NutsIcon,
  WheatIcon,
  FishIcon,
} from '../../icons';

/* ---- Layout constants ---- */
const GRID_CLASSES = 'grid grid-cols-4 gap-4';

/* ---- Chip classes ---- */
const CHIP_BASE = 'flex flex-col items-center justify-center gap-[3px] h-[66px] border rounded-input cursor-pointer select-none outline-none transition-[color,background-color,border-color,transform,opacity] ease-in-out active:scale-95 focus-visible:border-brand-yellow font-inter text-sm font-normal leading-normal text-center';

/* ---- Types ---- */
interface AllergenTagFilterBodyProps {
  selectedTags: string[];
  onToggle: (tag: string) => void;
}

interface AllergenTag {
  id: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}

/* ---- Data ---- */
// Mirrors PreDefinedFoodTags::Allergen::ALL (app/models/concerns/pre_defined_food_tags.rb)
const ALLERGEN_TAGS: AllergenTag[] = [
  { id: 'allium', label: 'allium', Icon: AlliumIcon },
  { id: 'barley', label: 'barley', Icon: BarleyIcon },
  { id: 'dairy', label: 'dairy', Icon: DairyIcon },
  { id: 'egg', label: 'egg', Icon: EggIcon },
  { id: 'shellfish', label: 'shellfish', Icon: ShellFishIcon },
  { id: 'soy', label: 'soy', Icon: SoyIcon },
  { id: 'nuts', label: 'nuts', Icon: NutsIcon },
  { id: 'wheat', label: 'wheat', Icon: WheatIcon },
  { id: 'fish', label: 'fish', Icon: FishIcon },
];

/* ---- Component ---- */
export function AllergenTagFilterBody({ selectedTags, onToggle }: AllergenTagFilterBodyProps) {
  const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags]);

  return (
    <div className="flex flex-col gap-4">
      <span className="font-inter text-sm font-semibold leading-normal text-grey">
        Allergens
      </span>
      <div className={GRID_CLASSES}>
        {ALLERGEN_TAGS.map(({ id, label, Icon }) => {
          const isSelected = selectedSet.has(id);
          return (
            <button
              key={id}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              className={cn(CHIP_BASE, isSelected ? CHIP_SELECTED : CHIP_UNSELECTED)}
              onClick={() => onToggle(id)}
            >
              <Icon className="h-[14px] w-auto" />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
